import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import {
  useTriggerPotStatusTransitions,
  useTriggerInvoiceGeneration,
  useProcessNotificationDefinitions,
  useProcessPushNotifications,
} from '../../hooks/useJobs';
import { UseMutationResult } from '@tanstack/react-query';
import { PermissionGate, Permissions } from '@billda/permissions-ui';

interface JobTriggerCardProps {
  title: string;
  description: string;
  mutation: UseMutationResult<{ message: string }, Error, void>;
}

const JobTriggerCard: React.FC<JobTriggerCardProps> = ({
  title,
  description,
  mutation,
}) => {
  // Auto-clear success/error after 5 seconds
  React.useEffect(() => {
    if (mutation.isSuccess || mutation.isError) {
      const timer = setTimeout(() => {
        mutation.reset();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [mutation.isSuccess, mutation.isError, mutation]);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={
            mutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <PlayArrowIcon />
            )
          }
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          sx={{ minWidth: 200 }}
        >
          {mutation.isPending ? 'Running Job...' : 'Trigger Job'}
        </Button>

        {mutation.isSuccess && mutation.data && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{ flex: 1, py: 0.5 }}
          >
            {mutation.data.message}
          </Alert>
        )}

        {mutation.isError && (
          <Alert
            severity="error"
            icon={<ErrorIcon />}
            sx={{ flex: 1, py: 0.5 }}
          >
            {mutation.error instanceof Error
              ? mutation.error.message
              : 'Failed to trigger job'}
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

export const JobsDebugSection: React.FC = () => {
  const potTransitionsMutation = useTriggerPotStatusTransitions();
  const invoiceGenerationMutation = useTriggerInvoiceGeneration();
  const notificationDefinitionsMutation = useProcessNotificationDefinitions();
  const pushNotificationsMutation = useProcessPushNotifications();

  return (
    <Stack spacing={3}>
      {/* Header */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <WorkIcon color="primary" />
          <Typography variant="h6">Background Jobs</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Manually trigger background job processing for testing and development
        </Typography>
      </Box>

      <PermissionGate
        permission={Permissions.Developer.Jobs.PotTransition.Trigger}
        showRequiredPermissions={false}
      >
        <JobTriggerCard
          title="Pot Status Transitions"
          description="Processes all pots that need status transitions based on current time and deadlines. This job normally runs on a schedule but can be triggered manually here for testing."
          mutation={potTransitionsMutation}
        />
      </PermissionGate>

      <PermissionGate
        permission={Permissions.Developer.Jobs.InvoiceGeneration.Trigger}
        showRequiredPermissions={false}
      >
        <JobTriggerCard
          title="Invoice Generation"
          description="Generates invoices for all pots that are ready for invoicing. This job normally runs on a schedule but can be triggered manually here for testing."
          mutation={invoiceGenerationMutation}
        />
      </PermissionGate>

      <PermissionGate
        permission={Permissions.Developer.Jobs.NotificationDefinitions.Trigger}
        showRequiredPermissions={false}
      >
        <JobTriggerCard
          title="Process Notification Definitions"
          description="Expands pending notification definitions into per-device push notification records. Signals the definition processor background job to check for pending work."
          mutation={notificationDefinitionsMutation}
        />
      </PermissionGate>

      <PermissionGate
        permission={Permissions.Developer.Jobs.PushNotifications.Trigger}
        showRequiredPermissions={false}
      >
        <JobTriggerCard
          title="Dispatch Push Notifications"
          description="Sends pending push notifications to devices via the notification hub. Signals the push dispatcher background job to check for pending push notifications."
          mutation={pushNotificationsMutation}
        />
      </PermissionGate>
    </Stack>
  );
};
